import { Box, CircularProgress, Divider, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import Paper from 'components/paper';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';

import { usePost } from 'hooks/use-post';

import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import OverviewSection from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-setup-tab/overview/overview-section';
import ProductionInputsSection from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-setup-tab/overview/product-inputs-section';
import SectionFishData from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-setup-tab/overview/section-fish-data';
import StepFooter from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-setup-tab/step-footer';

import { useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { putSetupOverview } from 'views/bluviu-nutrition/features/scenarios-v2/api/scenario-put';
import { useR2ScenarioState } from 'views/bluviu-nutrition/features/scenarios-v2/context/r2-scenario-context';
import { MortalityProfile } from 'views/bluviu-nutrition/features/scenarios-v2/types/mortality-profile';
import { R2ScenarioParams } from 'views/bluviu-nutrition/features/scenarios-v2/types/r2-scenario-params.type';
import { ScenarioGeneration } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-generation.type';
import { ScenarioSetupStepProps } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-setup-step-props.type';
import { Scenario } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario.type';
import {
  UpdateScenarioOverviewCageDataInput,
  UpdateScenarioOverviewInput,
} from 'views/bluviu-nutrition/features/scenarios-v2/types/update-scenario-overview-input.type';
import { getFishGroupValidationState } from 'views/bluviu-nutrition/features/scenarios-v2/utils/fish-group-utils';

interface ScenarioOverviewForm {
  calibration: { id: string; name: string };
  endDate: string;
  generation: ScenarioGeneration;
  mortalityProfile: MortalityProfile;
  name: string;
  cageData: UpdateScenarioOverviewCageDataInput[];
}

const StepOverview: React.FC<ScenarioSetupStepProps> = ({ onClick, activeStep, isFirstStep, canProceed }) => {
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();
  const params = useParams<R2ScenarioParams>();
  const intl = useIntl();

  const {
    r2ScenarioQuery: { data: scenario },
  } = useR2ScenarioState();

  const hasReset = useRef(false);

  const defaultValues: DefaultValues<ScenarioOverviewForm> = useMemo(() => {
    if (!scenario) {
      return {
        calibration: { id: '', name: '' },
        endDate: '',
        generation: undefined,
        mortalityProfile: { id: 0, name: '', isActive: false },
        name: '',
        cageData: [],
      };
    }

    return {
      calibration: { id: scenario.calibrationId ?? '', name: scenario.calibrationName ?? '' },
      endDate: scenario.endDate,
      generation: scenario.generation ?? undefined,
      mortalityProfile: {
        id: scenario.scenarioDefaults.mortalityProfileId ?? 0,
        name: scenario.scenarioDefaults.mortalityProfileName ?? '',
        isActive: true,
      },
      name: scenario.name,
      cageData: scenario.cageData.map((cage) => ({
        id: cage.id,
        isIncluded: cage.isIncluded,
        isUserEdited: cage.isUserEdited,
        isVirtual: cage.isVirtual,
        startDate: cage.startDate,
      })),
    };
  }, [scenario]);

  const form = useForm<ScenarioOverviewForm>({
    defaultValues,
  });

  useEffect(() => {
    if (hasReset.current || !scenario) return;
    form.reset(defaultValues);
    hasReset.current = true;
  }, [scenario]);

  const updateOverview = usePost<Scenario, UpdateScenarioOverviewInput>(
    (request) => putSetupOverview(params.scenarioId, params.globalSiteId, request),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      queryClient.invalidateQueries({
        queryKey: ['getR2Scenario', params.globalSiteId, params.scenarioId],
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const isDirty = form.formState.isDirty;
  const watchedCageData = form.watch('cageData');
  const watchedEndDate = form.watch('endDate');

  const hasInvalidIncludedCage =
    scenario?.cageData.some((cage, index) => {
      const watchedCage = watchedCageData[index];
      if (!watchedCage?.isIncluded) return false;

      return getFishGroupValidationState({ ...cage, startDate: watchedCage.startDate }, watchedEndDate).isInvalid;
    }) ?? false;

  const handleUpdate = (data: ScenarioOverviewForm, isSave: boolean) => {
    if (hasInvalidIncludedCage) return;
    if (!isDirty && !!scenario && !isSave) {
      onClick(activeStep + 1);
      return;
    }

    const overviewInput: UpdateScenarioOverviewInput = {
      endDate: data.endDate,
      name: data.name,
      calibrationId: data.calibration.id,
      calibrationName: data.calibration.name,
      generationSeason: data.generation.season,
      generationYear: data.generation.year,
      mortalityProfileId: data.mortalityProfile.id,
      mortalityProfileName: data.mortalityProfile.name,
      cageData: data.cageData.map((cage) => ({
        id: cage.id,
        isIncluded: cage.isIncluded,
        isUserEdited: cage.isUserEdited,
        isVirtual: cage.isVirtual,
        startDate: cage.startDate,
      })),
    };

    updateOverview.mutate(
      overviewInput,
      isSave
        ? undefined
        : {
            onSuccess: () => {
              onClick(activeStep + 1);
            },
          }
    );
  };

  const handleNext = form.handleSubmit((data) => handleUpdate(data, false));

  // Surgically update cage data if scenario's cage data changes, e.g. from syncing.
  useEffect(() => {
    if (!scenario?.cageData) return;
    form.setValue(
      'cageData',
      scenario.cageData.map((cage) => ({
        id: cage.id,
        isIncluded: cage.isIncluded,
        isUserEdited: cage.isUserEdited,
        isVirtual: cage.isVirtual,
        startDate: cage.startDate,
      }))
    );
  }, [scenario?.cageData]);

  return (
    <Paper sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 4, height: '100%' }}>
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Typography>Overview inputs for scenario</Typography>
        <Divider flexItem />
        <FormProvider {...form}>
          <OverviewSection />
          <Divider />
          <Box display="flex" flexDirection="column" gap={3}>
            <SectionFishData />
            <Divider />
            <ProductionInputsSection />
          </Box>
          <Divider />
          <StepFooter
            onPrev={() => onClick(activeStep - 1)}
            onNext={handleNext}
            onSkip={() => onClick(activeStep + 1)}
            disablePrev={isFirstStep}
            disableNext={!canProceed || updateOverview.isPending}
            disableSave
            nextIcon={updateOverview.isPending ? <CircularProgress size={20} /> : undefined}
          />
        </FormProvider>
      </Box>
    </Paper>
  );
};

export default StepOverview;
